import React, { useEffect } from 'react';
import { Form, Row, Col, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const GoalsObjectives = ({ goals, onChange, age }) => {
  // Auto-populate time horizon when component mounts or age changes
  useEffect(() => {
    if (age && goals.targetRetirementAge) {
      const calculatedYears = Math.max(0, goals.targetRetirementAge - age);
      if (goals.yearsToRetirement !== calculatedYears) {
        onChange({ ...goals, yearsToRetirement: calculatedYears });
      }
    }
  }, [age]); // Only on age change, not every render

  const handleChange = (field, value) => {
    const newGoals = { ...goals, [field]: value };

    // Auto-calculate years to retirement when target age changes
    if (field === 'targetRetirementAge') {
      newGoals.yearsToRetirement = Math.max(0, value - age);
    }

    onChange(newGoals);
  };

  // Retirement Living Standards reference amounts (2024 figures)
  const retirementStandards = {
    minimum: { single: 14400, couple: 22400 },
    moderate: { single: 31300, couple: 43100 },
    comfortable: { single: 43100, couple: 59000 }
  };

  return (
      <Row>
        {/* Target Retirement Age */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon warning">
              <FontAwesomeIcon icon="calendar-alt" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>When would you like the option to retire?</Form.Label>
              <InputGroup>
                <InputGroup.Text>Age</InputGroup.Text>
                <Form.Control
                    type="number"
                    value={goals.targetRetirementAge ?? ''}
                    onChange={(e) => handleChange('targetRetirementAge', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('targetRetirementAge', 0); }}
                    min={age}
                    max={100}
                />
              </InputGroup>
              <Form.Text className="text-muted">
                This gives you approximately <strong>{goals.yearsToRetirement} years</strong> until retirement
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Years to Retirement (auto-populated, editable) */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="clock" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Investment time horizon</Form.Label>
              <InputGroup>
                <Form.Control
                    type="number"
                    value={goals.yearsToRetirement ?? ''}
                    onChange={(e) => handleChange('yearsToRetirement', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('yearsToRetirement', 0); }}
                    min={1}
                    max={50}
                />
                <InputGroup.Text>years</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                Auto-calculated from your age ({age}) and retirement age ({goals.targetRetirementAge}).
                Adjust if needed.
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Primary Investment Goal */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon success">
              <FontAwesomeIcon icon="bullseye" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Which statement best describes your objectives?</Form.Label>
              <Form.Select
                  value={goals.primaryGoal}
                  onChange={(e) => handleChange('primaryGoal', e.target.value)}
              >
                <option value="stability">Stability is more important than higher returns</option>
                <option value="cautious_growth">I want higher returns than cash, with infrequent falls</option>
                <option value="balanced_growth">I want to beat inflation, accepting occasional extended falls</option>
                <option value="max_growth">I want the best long-term returns, accepting extended falls</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Col>

        {/* Target Retirement Income */}
        <Col md={6}>
          <div className="profile-card">
            <div className="card-icon primary">
              <FontAwesomeIcon icon="pound-sign" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>What annual income would you want in retirement?</Form.Label>
              <InputGroup>
                <InputGroup.Text>£</InputGroup.Text>
                <Form.Control
                    type="number"
                    value={goals.incomeInRetirement ?? ''}
                    onChange={(e) => handleChange('incomeInRetirement', e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => { if (e.target.value === '') handleChange('incomeInRetirement', 0); }}
                    min={0}
                    step={1000}
                />
                <InputGroup.Text>per year</InputGroup.Text>
              </InputGroup>
              <Form.Text className="text-muted">
                In today's money (before inflation).{' '}
                <a
                    href="https://www.retirementlivingstandards.org.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-info"
                >
                  Retirement Living Standards
                </a>
                : Minimum £{retirementStandards.minimum.single.toLocaleString()} · Moderate £{retirementStandards.moderate.single.toLocaleString()} · Comfortable £{retirementStandards.comfortable.single.toLocaleString()} (single, per year)
              </Form.Text>
            </Form.Group>
          </div>
        </Col>

        {/* Retirement Living Standards Reference */}
        <Col md={12}>
          <div className="profile-card d-flex flex-column justify-content-center" style={{ backgroundColor: '#f0f9ff', border: '1px solid #bee3f8' }}>
            <div className="d-flex align-items-start">
              <FontAwesomeIcon icon="info-circle" className="me-2 mt-1 text-info flex-shrink-0" />
              <div style={{ width: '100%' }}>
                <strong className="small">PLSA Retirement Living Standards (2024)</strong>
                <div className="mt-2" style={{ fontSize: '0.85rem', maxWidth: 400 }}>
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Minimum</span>
                    <span>£{retirementStandards.minimum.single.toLocaleString()} / £{retirementStandards.minimum.couple.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Moderate</span>
                    <span>£{retirementStandards.moderate.single.toLocaleString()} / £{retirementStandards.moderate.couple.toLocaleString()}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">Comfortable</span>
                    <span>£{retirementStandards.comfortable.single.toLocaleString()} / £{retirementStandards.comfortable.couple.toLocaleString()}</span>
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>Single / Couple · per year</div>
                </div>
              </div>
            </div>
          </div>
        </Col>

        {/* Essential vs Aspirational */}
        <Col md={12}>
          <div className="profile-card">
            <div className="card-icon warning">
              <FontAwesomeIcon icon="exclamation-circle" />
            </div>
            <Form.Group className="mb-3">
              <Form.Label>How would you describe your retirement goal?</Form.Label>
              <Form.Select
                  value={goals.goalType || 'important'}
                  onChange={(e) => handleChange('goalType', e.target.value)}
              >
                <option value="essential">Essential — I must achieve this, cannot put it at risk</option>
                <option value="important">Important — I want to achieve this, but have some flexibility</option>
                <option value="aspirational">Aspirational — Would be nice, but not critical</option>
              </Form.Select>
              <Form.Text className="text-muted">
                This helps us understand how much risk is appropriate for your situation
              </Form.Text>
            </Form.Group>
          </div>
        </Col>
      </Row>
  );
};

export default GoalsObjectives;